import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../components/Icon';
import MonasteryCard from '../components/MonasteryCard';
import PhotoCredit from '../components/PhotoCredit';
import Embers from '../components/Embers';
import Reveal from '../components/Reveal';
import RegionMap from '../components/RegionMap';
import MonasteryProfileMockup from '../components/MonasteryProfileMockup';
import { CardGridSkeleton } from '../components/ui';
import { api } from '../lib/api';
import { trackEvent } from '../lib/consent';

const STEP_ICONS = { compass: 'compass', flame: 'flame', quill: 'quill', seal: 'sparkles', video: 'video' };

/**
 * Key, visible label, and the one-sentence breakdown shown when a reader
 * taps the row — kept as data (not five near-identical JSX blocks) so the
 * accordion below is one `.map`, matching how `content?.faq` already drives
 * the FAQ list.
 */
const TRUST_BULLETS = [
  ['verifiedInfo', 'Verified monastery information',
    'We confirm details directly with the monastery or an authorized representative before anything goes live on GOMPA.'],
  ['officialChannels', 'Official donation channels',
    "Every donation link is checked against the monastery's own published channels before it appears here."],
  ['transparentSupport', 'Transparent support',
    'You always know exactly where a contribution goes — a GOMPA-facilitated offering, or a link straight to the monastery.'],
  ['monasteryControlled', 'Monastery-controlled offerings',
    'Each monastery decides what it lists and what it charges, and can update or remove an offering at any time.'],
  ['directRemainsDirect', 'Direct support remains direct',
    "A direct donation bypasses GOMPA entirely — funds move straight from you to the monastery's own donation page."],
];

/**
 * Sets the loanword `chöme` in italic wherever the headline uses it.
 *
 * Done at render rather than by putting markup in the strings: the eight
 * locale files stay plain text, and the locales that translate the word
 * instead of borrowing it — most of them say "butter lamp" — simply match
 * nothing and render unchanged. Tibetan, which has no Latin text at all, is
 * untouched by the split.
 */
function withChomeEmphasis(text) {
  return String(text)
    .split(/(chöme)/i)
    .map((part, index) =>
      /^chöme$/i.test(part) ? (
        <em className="hero__term" key={index}>{part}</em>
      ) : (
        part
      )
    );
}
const PILLAR_ICONS = { wheel: 'wheel', script: 'language', lotus: 'lotus', shield: 'shield' };

export default function Landing() {
  const { t } = useTranslation();
  const [content, setContent] = useState(null);
  const [monasteries, setMonasteries] = useState(null);
  const [allMonasteries, setAllMonasteries] = useState([]);
  const [totalMonasteries, setTotalMonasteries] = useState(null);
  const [lineages, setLineages] = useState([]);
  const [openFaq, setOpenFaq] = useState(0);
  // Independent, not exclusive: these five are separate facts, not
  // alternatives, so more than one can be open at a time (unlike the FAQ).
  const [openTrustItems, setOpenTrustItems] = useState(() => new Set());
  const toggleTrustItem = (key) => {
    setOpenTrustItems((prev) => {
      const next = new Set(prev);
      const opening = !next.has(key);
      if (opening) next.add(key); else next.delete(key);
      trackEvent('trust_item_toggle', { item_key: key, opened: opening });
      return next;
    });
  };

  useEffect(() => {
    const controller = new AbortController();
    Promise.all([
      api.landing(controller.signal),
      api.monasteries({}, controller.signal),
      api.lineages(controller.signal),
    ])
      .then(([landing, list, lineageList]) => {
        setContent(landing);
        setTotalMonasteries(list.total);
        setAllMonasteries(list.monasteries);
        setMonasteries(list.monasteries.slice(0, 3));
        setLineages(lineageList.lineages);
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!monasteries) return;
    trackEvent('view_item_list', { item_list_name: 'landing', item_count: monasteries.length });
  }, [monasteries]);

  const hero = content?.hero;
  const how = content?.howItWorksSection;
  const heroMonastery = monasteries?.[0];
  const heroPhoto = heroMonastery?.photo;
  const withPhotos = allMonasteries.filter((monastery) => monastery.photo?.source);
  const crests = withPhotos.slice(0, 5);
  const crestOverflow = withPhotos.length - crests.length;

  return (
    <>
      <section className="hero hero--awakening">
        {heroPhoto?.source && (
          <img
            className="hero__photo"
            src={heroPhoto.source}
            alt=""
            aria-hidden="true"
            fetchPriority="high"
          />
        )}
        <div className="hero__scrim" />
        {/* The readability veil, held at full strength while the scrim above it
            animates — see `.hero__veil` for why the two are separate layers. */}
        <div className="hero__veil" aria-hidden="true" />
        {/* The ignition: a warm radial bloom that opens behind the CTA column. */}
        <div className="hero__bloom" aria-hidden="true" />
        {/* The lamps: a warm ellipse breathing at the foot of the frame. */}
        <div className="hero__altar-glow" aria-hidden="true" />
        <Embers />

        <div className="shell hero__inner">
          {hero?.tibetan && (
            <p className="tibetan hero__tibetan tibetan--on-dark hero__unfurl" style={{ '--unfurl-delay': '0.80s' }} lang="bo">
              {hero.tibetan}
            </p>
          )}

          <h1 className="display display--xl hero__title hero__unfurl" style={{ '--unfurl-delay': '0.88s' }}>
            {withChomeEmphasis(
              t('landing.heroTitle', hero?.title || 'Offer a Chöme and sacred prayers at Buddhist monasteries.')
            )}
          </h1>

          <p className="hero__body hero__unfurl" style={{ '--unfurl-delay': '1.00s' }}>
            {t(
              'landing.heroBody',
              hero?.body ||
                'Sponsor traditional butter lamps and pujas performed on your behalf by resident monks and nuns — from wherever you are.'
            )}
          </p>

          <div className="hero__ctas hero__unfurl" style={{ '--unfurl-delay': '1.12s' }}>
            <Link
              to={hero?.primaryCta?.to || '/explore'}
              className="btn btn--gold btn--lg hero__cta-primary"
              onClick={() => trackEvent('cta_click', { cta_id: 'hero' })}
            >
              <Icon name="flame" size={18} solid />
              {t('landing.heroPrimaryCta', hero?.primaryCta?.label || 'Make an offering')}
            </Link>
          </div>

          <div className="hero__trust hero__unfurl" style={{ '--unfurl-delay': '1.24s' }}>
            {/*
              The crests are the actual monasteries, photographed — not an
              invented seal per lineage. A real place a reader can go and check
              is the credential; a drawn emblem would be decoration claiming to
              be one. These are the same CC BY-SA photographs used everywhere
              else on the site, which is why the attribution line at the foot
              of the page points at /credits.
            */}
            <div className="hero__seal-row">
            {crests.length > 0 && (
              <Link
                to="/explore"
                className="crest-cluster"
                aria-label={t('landing.heroCrestsAria', {
                  count: withPhotos.length,
                  defaultValue: `Explore all ${withPhotos.length} partner monasteries`,
                })}
                onClick={() => trackEvent('cta_click', { cta_id: 'crest_cluster' })}
              >
                {crests.map((monastery) => (
                  <span className="crest" key={monastery.id} title={monastery.name} aria-hidden="true">
                    <img src={monastery.photo.source} alt="" loading="lazy" />
                  </span>
                ))}
                {crestOverflow > 0 && (
                  <span className="crest crest--more" aria-hidden="true">+{crestOverflow}</span>
                )}
              </Link>
            )}
            <p className="trust-pill shimmer">
              <Icon name="sparkles" size={14} solid />
              {t('landing.heroTrustBadge', hero?.trustBadge || 'Authorized Monastery Affiliate')}
            </p>
            </div>
            <p className="hero__scope">
              {t('landing.heroScope', hero?.scope || 'Blessed and officially authorized by Thrangu Tashi Yangtse Monastery, of the Karma Kagyu tradition.')}
            </p>
          </div>
        </div>
      </section>

      {/* ----------------------------------------------------- how it works -- */}
      {/*
        The sheet. It is pulled up over the foot of the hero and rounded, so the
        page reads as a parchment guide being drawn up over the shrine rather
        than as a bright rectangle butted against a dark one. The wheel sits on
        the seam as the buffer between the two.
      */}
      <section className="section section--sheet" id="how">
        <span className="sheet-seal" aria-hidden="true">
          <Icon name="wheel" size={20} />
        </span>

        <div className="shell">
          <Reveal as="div" className="section-head">
            <p className="eyebrow">{t('landing.howItWorksEyebrow', how?.eyebrow || 'How an offering works')}</p>
            <h2 className="display display--lg">
              {t('landing.howItWorksTitle', how?.title || 'From your screen to the shrine room')}
            </h2>
          </Reveal>

          <ol className="steps">
            {(content?.howItWorks || []).map((step, idx) => (
              <li
                className={`step${step.emphasis ? ' step--payoff' : ''}`}
                key={step.step}
              >
                <span className="step__node" aria-hidden="true">{step.step}</span>

                <div className="step__body">
                  <h3 className="step__title">
                    <Icon name={STEP_ICONS[step.icon] || 'lotus'} size={17} />
                    {t(`landing.steps.step${idx + 1}Title`, step.title)}
                  </h3>
                  <p>{t(`landing.steps.step${idx + 1}Body`, step.body)}</p>

                  {step.pending && (
                    <p className="step__pending">
                      <Icon name="info" size={13} />
                      {step.pending}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>

          {how?.note && (
            <p className="steps-note">
              <Icon name="time" size={15} />
              {how.note}
            </p>
          )}
        </div>
      </section>

      {/* ---------------------------------------------------- the monasteries -- */}
      <section className="section section--paper">
        <div className="shell">
          <Reveal as="div" className="section-head">
            <p className="eyebrow">{t('landing.monasteriesEyebrow', 'The monasteries')}</p>
            <h2 className="display display--lg">{t('landing.monasteriesTitle', 'Where your offering is made.')}</h2>
            <p className="lede">
              {t('landing.monasteriesLede', 'Two Karma Kagyu monasteries to start — Thrangu Tashi Yangtse, above the Namo Buddha pilgrimage site, and Thrangu Tashi Choling, facing the Great Stupa of Boudhanath, both founded by the 9th Khenchen Thrangu Rinpoche. GOMPA is open to every tradition: monasteries of any lineage are welcome, and more are on their way.')}
            </p>
          </Reveal>

          {monasteries ? (
            <div className="grid-cards">
              {monasteries.map((monastery) => (
                <MonasteryCard key={monastery.id} monastery={monastery} listName="landing" />
              ))}
            </div>
          ) : (
            <CardGridSkeleton count={3} />
          )}

          {/*
            The growing-list note. Placed after the cards rather than beside the
            heading so the flow reads as one movement: see who is here, then
            hear that the circle is widening, then go look for yourself. It
            states an intention, not a number or a date — we are not
            promising a count of monasteries we do not yet have signed.
          */}
          <div className="monasteries-more">
            <p className="monasteries-more__note">
              <Icon name="lotus" size={16} />
              {t(
                'landing.monasteriesMore',
                'More monasteries are joining GOMPA, so more disciples can stay close to their own — wherever they are.'
              )}
            </p>
            <Link to="/explore" className="btn btn--ghost">
              {t('landing.seeAll', {
                count: totalMonasteries ?? 1,
                defaultValue:
                  (totalMonasteries ?? 1) === 1
                    ? 'See the monastery'
                    : `See all ${totalMonasteries ?? 1} monasteries`,
              })}
              <Icon name="arrowRight" size={17} />
            </Link>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------- pillars -- */}
      <section className="section">
        <div className="shell">
          <Reveal as="div" className="section-head section-head--center">
            <p className="eyebrow">{t('landing.pillarsEyebrow', 'What you can expect')}</p>
            <h2 className="display display--lg">{t('landing.pillarsTitle', 'A shrine, as it should be.')}</h2>
          </Reveal>

          <div className="pillars">
            {(content?.pillars || []).map((pillar, index) => (
              <Reveal as="div" className="card pillar" key={pillar.title} delay={index * 90}>
                <span className="pillar__plate">
                  <Icon name={PILLAR_ICONS[pillar.icon] || 'lotus'} size={20} />
                </span>
                <h3>{t(`landing.pillars.p${index + 1}Title`, pillar.title)}</h3>
                <p>{t(`landing.pillars.p${index + 1}Body`, pillar.body)}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------- traditions -- */}
      <section className="section section--paper">
        <div className="shell">
          <Reveal as="div" className="section-head section-head--center">
            <p className="eyebrow">{t('landing.lineagesEyebrow', 'Lineages')}</p>
            <h2 className="display display--lg">{t('landing.lineagesTitle', 'Find your tradition.')}</h2>
            <p className="lede">
              {t('landing.lineagesLede', 'Nyingma, Kagyu, Sakya, Gelug, Jonang and Bön. Every tradition has its own monasteries, liturgies and calendar.')}
            </p>
          </Reveal>

          <div className="lineage-strip">
            {lineages.map((lineage, index) => (
              <Reveal
                as={Link}
                className="lineage-chip"
                key={lineage.key}
                to={`/explore?lineage=${lineage.key}`}
                delay={index * 70}
              >
                <span className="lineage-chip__tib tibetan" lang="bo">{lineage.tibetanName}</span>
                <span className="lineage-chip__name">{lineage.name}</span>
                <span className="lineage-chip__sub">{lineage.subtitle}</span>
                <span
                  className={`lineage-chip__count${lineage.monasteryCount ? '' : ' lineage-chip__count--none'}`}
                >
                  {lineage.monasteryCount
                    ? t('landing.traditionCount', { count: lineage.monasteryCount, defaultValue: `${lineage.monasteryCount} listed` })
                    : t('landing.traditionNone', 'none listed yet')}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------- diaspora -- */}
      <section className="section section--night-tint">
        <div className="shell shell--narrow">
          <Reveal as="div" className="section-head section-head--center">
            <p className="eyebrow">{t('landing.diasporaEyebrow', 'Connection & continuity')}</p>
            <h2 className="display display--lg">
              {t('landing.diasporaTitle', 'Thousands of miles away. Still connected.')}
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <RegionMap />
          </Reveal>
          <div className="stack" style={{ textAlign: 'center', maxWidth: '60ch', marginInline: 'auto' }}>
            <p className="lede">
              {t(
                'landing.diasporaLine1',
                "Whether you grew up visiting a local gompa or your family still supports one back home, moving to a new city shouldn't mean growing distant from your tradition."
              )}
            </p>
            <p className="lede" style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
              {t(
                'landing.diasporaLine2',
                'GOMPA bridges the distance—making it simple to request prayers, sponsor pujas, and support your monastery from anywhere.'
              )}
            </p>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------ trust -- */}
      <section className="section section--gold-tint">
        <div className="shell shell--narrow">
          <Reveal as="div" className="section-head section-head--center">
            {/* Was "Trust comes first" over "Trust comes first." — the eyebrow
                echoed the headline instead of doing an eyebrow's actual job
                (telling the reader what kind of claim is coming). This one
                names the claim; the headline still gets to make it. */}
            <p className="eyebrow">{t('landing.trustEyebrow', 'Authenticity & integrity')}</p>
            <h2 className="display display--lg">{t('landing.trustTitle', 'Trust comes first.')}</h2>
            <p className="lede">
              {t(
                'landing.trustBody',
                "We're building GOMPA slowly. We verify monastery information, work directly with monasteries and authorized representatives, and clearly distinguish verified monasteries from information that has not yet been confirmed."
              )}
            </p>
          </Reveal>

          {/* A bordered, off-white card rather than a bare list floating on
              the tinted section background — these are verification
              guarantees, not a bulleted summary, and a card gives them the
              same visual weight the record/contact facts get elsewhere on
              the site. Each point gets its own small gold shield plate
              instead of a plain checkmark, echoing the verification badge's
              own iconography rather than borrowing e-commerce checklist
              styling — and is independently tappable to read the one
              sentence behind it, so "trust us" comes with an actual
              explanation on demand instead of five bare assertions. */}
          <Reveal as="div" className="card card__pad verify-card" style={{ maxWidth: 480, marginInline: 'auto' }}>
            {TRUST_BULLETS.map(([key, fallback, detailFallback], index) => {
              const open = openTrustItems.has(key);
              return (
                <Reveal
                  as="div"
                  className={`verify-item${open ? ' is-open' : ''}`}
                  key={key}
                  delay={index * 90}
                >
                  <button
                    type="button"
                    className="verify-item__q"
                    aria-expanded={open}
                    onClick={() => toggleTrustItem(key)}
                  >
                    <span className="verify-plate" aria-hidden="true">
                      <Icon name="shield" size={12} />
                    </span>
                    <span>{t(`landing.trustBullets.${key}`, fallback)}</span>
                    <Icon className="verify-item__chevron" name="chevronDown" size={16} />
                  </button>
                  <div className="verify-item__detail-wrap">
                    <div className="verify-item__detail-inner">
                      <p className="verify-item__detail">
                        {t(`landing.trustBullets.${key}Detail`, detailFallback)}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </Reveal>
        </div>
      </section>

      {/* ------------------------------------------------------ starting small -- */}
      {/* The one section on the page that isn't a centered
          eyebrow→headline→lede block — after eight of those in a row, this
          one breaks the rhythm on purpose: text left, two real numbers
          right (the same `totalMonasteries` and `lineages` this page
          already fetched for the cards and the lineage strip above — no
          new stat invented for the occasion). */}
      <section className="section section--rose-tint">
        <div className="shell">
          <div className="starting-small">
            <Reveal as="div" className="starting-small__text">
              <p className="eyebrow">{t('landing.startingSmallEyebrow', 'Starting small')}</p>
              <h2 className="display display--lg">
                {t('landing.startingSmallTitle', "We're building this one monastery at a time.")}
              </h2>
              <p className="lede">
                {t(
                  'landing.startingSmallBody',
                  "GOMPA is starting with Buddhist communities in the United States and Canada. Our goal isn't to list every monastery overnight. Our goal is to build a place you can trust."
                )}
              </p>
            </Reveal>
            <Reveal as="div" className="starting-small__stats" delay={100}>
              <div className="starting-small__stat">
                <span className="starting-small__stat-num">{totalMonasteries ?? '—'}</span>
                <span className="starting-small__stat-label">
                  {t('landing.startingSmallStatMonasteries', {
                    count: totalMonasteries ?? 0,
                    defaultValue: (totalMonasteries ?? 0) === 1 ? 'Monastery live today' : 'Monasteries live today',
                  })}
                </span>
              </div>
              <div className="starting-small__stat">
                <span className="starting-small__stat-num">{lineages.length || '—'}</span>
                <span className="starting-small__stat-label">
                  {t('landing.startingSmallStatTraditions', 'Traditions listed')}
                </span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* -------------------------------------------------------- claim cta -- */}
      <section className="section section--paper">
        <div className="shell shell--narrow">
          {/* The product preview leads here — seeing an actual verified
              profile does more to answer "what is this" for a monastery
              representative than another paragraph of copy would. */}
          <Reveal delay={60}>
            <MonasteryProfileMockup />
          </Reveal>
          <Reveal as="div" className="section-head section-head--center">
            <p className="eyebrow">{t('landing.claimCtaEyebrow', 'For monasteries')}</p>
            <h2 className="display display--lg">{t('landing.claimCtaTitle', 'Help Your Practitioners Stay Connected')}</h2>
            <p className="lede">
              {t('landing.claimCtaBody', "As a monastery representative, you can manage your monastery's information and stay connected with practitioners on GOMPA.")}
            </p>
            {/* Was btn--ghost: a white pill on this section's cream
                background had almost nothing to read against. Solid maroon
                gives it the same weight as the hero's primary CTA, plus its
                own hover lift since this is the one button on the page
                asking a monastery, not a donor, to act. */}
            <Link
              to="/explore"
              className="btn btn--primary btn--lg btn--lift"
              onClick={() => trackEvent('cta_click', { cta_id: 'claim_monastery' })}
            >
              {t('landing.claimCtaButton', 'Get Started')}
              <Icon name="arrowRight" size={17} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* --------------------------------------------------------------- faq -- */}
      <section className="section" id="faq">
        <div className="shell shell--narrow">
          <Reveal as="div" className="section-head">
            <p className="eyebrow">{t('landing.faqEyebrow', 'Questions')}</p>
            <h2 className="display display--lg">{t('landing.faqTitle', 'Before you offer.')}</h2>
          </Reveal>

          <div className="faq">
            {(content?.faq || []).map((item, index) => {
              const open = openFaq === index;
              return (
                <div className={`faq__item${open ? ' is-open' : ''}`} key={item.q}>
                  <h3>
                    <button
                      type="button"
                      className="faq__q"
                      aria-expanded={open}
                      onClick={() => {
                        setOpenFaq(open ? -1 : index);
                        trackEvent('faq_toggle', { question_index: index, opened: !open, location: 'landing' });
                      }}
                    >
                      {t(`landing.faq.q${index + 1}`, item.q)}
                      <Icon name="chevronDown" size={18} />
                    </button>
                  </h3>
                  {/* Always mounted, height animated via CSS rather than
                      conditionally rendered — a mount/unmount cannot
                      transition, which is why this used to snap open
                      instead of easing. */}
                  <div className="faq__a-wrap">
                    <div className="faq__a-inner">
                      <p className="faq__a">{t(`landing.faq.a${index + 1}`, item.a)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* --------------------------------------------------------------- cta -- */}
      <section className="section section--tight">
        <div className="shell">
          <div className="cta-panel">
            {/*
              Śāntideva, Bodhicaryāvatāra — the offering of the parasol, from
              the chapter on making offerings. Four lines, so the shad at the
              end of each is load-bearing: the Tibetan is preserved exactly as
              given, tsheg and shad included, and must not be "tidied".
            */}
            <p className="cta-panel__verse tibetan tibetan--on-dark" lang="bo">
              <span>རིན་ཆེན་གདུགས་མཛེས་གསེར་གྱི་ཡུ་བ་ཅན། །</span>
              <span>ཁོར་ཡུག་རྒྱན་གྱི་རྣམ་པ་ཡིད་འོང་བརྒྱན། །</span>
              <span>དབྱིབས་ལེགས་བལྟ་ན་སྡུག་པ་བསྒྲེང་བ་ཡང༌། །</span>
              <span>རྟག་ཏུ་ཐུབ་དབང་རྣམས་ལ་དབུལ་བར་བགྱི། །</span>
            </p>
            <p className="cta-panel__source tibetan tibetan--on-dark" lang="bo">སྤྱོད་འཇུག་ལས།</p>

            <h2 className="display display--md cta-panel__title">
              <span>{t('landing.ctaVerse1', 'Golden handles, jewels that shine,')}</span>
              <span>{t('landing.ctaVerse2', 'Beautiful parasols, graceful and bright.')}</span>
              <span>{t('landing.ctaVerse3', 'With all my heart, I make this offering')}</span>
              <span>{t('landing.ctaVerse4', 'To all the Buddhas, now and forever.')}</span>
            </h2>
            <p className="cta-panel__source">
              {t('landing.ctaVerseSource', 'from Bodhicaryāvatāra')}
            </p>
            <p>
              {t('landing.ctaBody', 'A butter lamp lit in a mountain shrine room, for someone you love, on the day that matters.')}
            </p>
            <Link to="/explore" className="btn btn--gold btn--lg" onClick={() => trackEvent('cta_click', { cta_id: 'bottom' })}>
              <Icon name="flame" size={18} solid />
              {t('landing.ctaButton', 'Choose a monastery')}
            </Link>
          </div>
        </div>
      </section>

      {/*
        The hero photograph's attribution.

        CC BY / CC BY-SA require the author and licence wherever the work is
        shown, so this cannot leave the page — it is a licence term, not a
        design element. What it can do is leave the hero: it now closes the
        page as a quiet line above the footer instead of sitting over the
        composition. The collected list is at /credits.
      */}
      {heroPhoto && (
        <div className="shell page-attribution">
          <PhotoCredit
            photo={{ ...heroPhoto, credit: `${heroMonastery.name} — ${heroPhoto.credit}` }}
          />
          {/* The hero credit above covers one photograph. The crest cluster and
              the cards put several more on this page, each under its own CC
              licence, so the collected list is linked from here too. */}
          <p className="credit">
            <span>{t('landing.allPhotoCredits', 'Monastery photographs by their Wikimedia Commons authors')}</span>
            <Link to="/credits">{t('landing.seeCredits', 'all credits')}</Link>
          </p>
        </div>
      )}
    </>
  );
}
